import { useState, useEffect } from 'react';
import { supabase } from '../providers/supabase/supabase';

interface RegressionProspectCounts {
    total: number;
    pending: number;
    contacted: number;
    completed: number;
}

interface RegressionProspect {
    id: number;
    artist_id: string;
    name: string;
    email: string;
    priority_score: number;
    momentum_score: number;
    readout_text: string;
    contacted_status: string;
    created_at: string;
    outreach_readout: string;
    reference_artist_name: string;
    reference_artist_listeners: number;
    reference_artist_2_name: string;
    reference_artist_2_listeners: number;
    reference_artist_3_name: string;
    reference_artist_3_listeners: number;
    spotify_monthly_listeners: number;
    spotify_followers: number;
    spotify_growth_rate: number;
}

export const useRegressionProspectCounts = () => {
    const [counts, setCounts] = useState<RegressionProspectCounts>({
        total: 0,
        pending: 0,
        contacted: 0,
        completed: 0
    });
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchCounts = async () => {
            setLoading(true);
            setError(null);

            try {
                const { data, error: queryError } = await supabase
                    .from('regression_prospects')
                    .select('contacted_status');

                if (queryError) throw queryError;

                if (!data) {
                    setCounts({ total: 0, pending: 0, contacted: 0, completed: 0 });
                    return;
                }

                const total = data.length;
                const pending = data.filter(item => item.contacted_status === 'pending').length;
                const contacted = data.filter(item => item.contacted_status === 'contacted').length;
                const completed = data.filter(item => item.contacted_status === 'completed').length;

                setCounts({ total, pending, contacted, completed });

            } catch (err) {
                console.error('Error fetching regression prospect counts:', err);
                setError(err instanceof Error ? err.message : 'Failed to fetch counts');
                setCounts({ total: 0, pending: 0, contacted: 0, completed: 0 });
            } finally {
                setLoading(false);
            }
        };

        fetchCounts();
    }, []);

    return { counts, loading, error };
};

export const useRegressionProspectsList = (status?: string) => {
    const [prospects, setProspects] = useState<RegressionProspect[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!status) return;

        const fetchProspects = async () => {
            setLoading(true);
            setError(null);

            try {
                let query = supabase
                    .from('regression_prospects')
                    .select('*')
                    .order('priority_score', { ascending: false });

                if (status !== 'all') {
                    query = query.eq('contacted_status', status);
                }

                const { data, error: queryError } = await query;

                if (queryError) throw queryError;

                setProspects(data || []);

            } catch (err) {
                console.error('Error fetching regression prospects:', err);
                setError(err instanceof Error ? err.message : 'Failed to fetch prospects');
                setProspects([]);
            } finally {
                setLoading(false);
            }
        };

        fetchProspects();
    }, [status]);

    return { prospects, loading, error };
};

// Hook to check if a specific artist is a regression prospect
export const useArtistRegressionStatus = (artistId: string | null | undefined) => {
    const [regressionData, setRegressionData] = useState<RegressionProspect | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!artistId) {
            setRegressionData(null);
            return;
        }

        const fetchRegressionStatus = async () => {
            setLoading(true);
            setError(null);

            try {
                const { data, error: queryError } = await supabase
                    .from('regression_prospects')
                    .select('*')
                    .eq('artist_id', artistId)
                    .single();

                if (queryError) {
                    if (queryError.code === 'PGRST116') {
                        // No data found - artist is not a regression prospect
                        setRegressionData(null);
                        return;
                    }
                    throw queryError;
                }

                setRegressionData(data);

            } catch (err) {
                console.error('Error fetching artist regression status:', err);
                setError(err instanceof Error ? err.message : 'Failed to fetch regression status');
                setRegressionData(null);
            } finally {
                setLoading(false);
            }
        };

        fetchRegressionStatus();
    }, [artistId]);

    return { regressionData, loading, error, isRegressionProspect: !!regressionData };
};